import { rootApi } from '@store/root-api';
import cdnApi from 'services/cdn-api';
import { objectKeysToLowerCase } from 'utils/casing';
import * as packageJson from './../../../package.json';
import { BundleInfo } from './types/BundleInfo';
import { PageConfig } from './types/PageConfig';
import { ProductDepositInfo, ProductsDepositInfoResponse } from './types/ProductDepositInfo';

async function fetchCdnData<T>(url: string) {
    const response = await cdnApi.get(url);
    if (response.data) {
        return { data: objectKeysToLowerCase(response.data) as T, error: undefined };
    }

    return {
        data: undefined,
        error: { message: `Fetching ${url} failed with status ${response.status}` },
    };
}

const cdnApiSlice = rootApi.injectEndpoints({
    endpoints: builder => ({
        getProductsDepositInfo: builder.query<ProductDepositInfo[], void>({
            queryFn: async () => {
                const res = await fetchCdnData<ProductsDepositInfoResponse>(
                    `data/products/products_depositable.json`,
                );
                if (res.error)
                    return res;

                return { data: res.data!.products };
            }
        }),
        getBundlesInfo: builder.query<BundleInfo[], void>({
            queryFn: async () => {
                const res = await fetchCdnData<BundleInfo[]>(
                    `${import.meta.env.VITE_ALTIFY_CDN_URL}data/bundles.json`,
                );
                if (res.error)
                    return res;

                return { data: res.data! };
            }
        }),
        getPageConfig: builder.query<PageConfig, void>({
            queryFn: () => fetchCdnData<PageConfig>(
                `/data/page_config.json?version=${packageJson.version}`,
            )
        })
    })
})

export function useGetBundleInfo(code: string) {
    return useGetBundlesInfoQuery(undefined, {
        selectFromResult: ({ data, isLoading }) => ({ data: data?.find(bundle => bundle.code === code), isLoading }),
        skip: !code
    });
}

export function useGetProductDepositInfo(code: string) {
    return useGetProductsDepositInfoQuery(undefined, {
        selectFromResult: ({ data, isLoading }) => ({ data: data?.find(product => product.code === code), isLoading }),
        skip: !code
    });
}

export const { useGetProductsDepositInfoQuery, useGetPageConfigQuery, useGetBundlesInfoQuery } = cdnApiSlice;
